import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Bitcoin,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  CheckCircle
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import './Checkout.css';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Checkout = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartId, setCartId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutStep, setCheckoutStep] = useState('cart'); // cart, payment, success
  const [paymentData, setPaymentData] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');

  useEffect(() => {
    initializeCheckout();
  }, [userId]);

  const initializeCheckout = async () => {
    try {
      // Fetch products
      const response = await axios.get(`${API_URL}/public/products/${userId}`);
      setProducts(response.data);

      // Create cart
      const cartResponse = await axios.post(`${API_URL}/cart?user_id=${userId}`);
      setCartId(cartResponse.data.cart_id);
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load checkout');
      setLoading(false);
    }
  };

  const addToCart = async (product) => {
    if (!cartId) return;

    try {
      await axios.post(`${API_URL}/cart/${cartId}/items?product_id=${product.id}&quantity=1`);
      
      const existingItem = cart.find(item => item.product_id === product.id);
      if (existingItem) {
        setCart(cart.map(item => 
          item.product_id === product.id 
            ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
            : item
        ));
      } else {
        setCart([...cart, {
          product_id: product.id,
          product_name: product.name,
          price: product.price,
          quantity: 1,
          total: product.price
        }]);
      }
      
      toast.success(`Added ${product.name}`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (!cartId) return;

    try {
      if (newQuantity <= 0) {
        await axios.delete(`${API_URL}/cart/${cartId}/items/${productId}`);
        setCart(cart.filter(item => item.product_id !== productId));
      } else {
        await axios.put(`${API_URL}/cart/${cartId}/items/${productId}?quantity=${newQuantity}`);
        setCart(cart.map(item => 
          item.product_id === productId 
            ? { ...item, quantity: newQuantity, total: newQuantity * item.price }
            : item
        ));
      }
    } catch (error) {
      toast.error('Failed to update cart');
    }
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.total, 0);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/cart/${cartId}/checkout${customerEmail ? `?customer_email=${customerEmail}` : ''}`
      );

      setPaymentData(response.data);
      setCheckoutStep('payment');
      
      // Start polling for payment status
      startPaymentPolling(response.data.invoice_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    }
  };

  const startPaymentPolling = (invoiceId) => {
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(`${API_URL}/payments/status/${invoiceId}`, { headers });
        
        if (response.data.payment_status === 'paid') {
          clearInterval(interval);
          setCheckoutStep('success');
        }
      } catch (error) {
        // Polling tick failed (network blip / 404 during status race) — keep polling.
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Checkout poll tick error (will retry):', error?.message || error);
        }
      }
    }, 3000);

    // Store interval ID to clear on unmount
    return () => clearInterval(interval);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (checkoutStep === 'success') {
    return (
      <div className="checkout-page success">
        <div className="checkout-container">
          <div className="success-card">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h1>Payment Successful!</h1>
            <p>Thank you for your purchase</p>
            <div className="success-amount">${calculateTotal().toFixed(2)}</div>
            {customerEmail && (
              <p className="email-notice">A receipt has been sent to {customerEmail}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (checkoutStep === 'payment') {
    return (
      <div className="checkout-page payment">
        <div className="payment-container">
          <div className="payment-header">
            <Bitcoin size={32} />
            <h1>Complete Payment</h1>
          </div>

          <div className="payment-amount-card">
            <span className="amount-label">Total Amount</span>
            <span className="amount-value">${paymentData.amount.toFixed(2)}</span>
          </div>

          <div className="qr-card">
            <p>Scan QR code with your crypto wallet</p>
            <div className="qr-container">
              <QRCodeSVG 
                value={paymentData.checkout_link}
                size={300}
                level="M"
                includeMargin={true}
              />
            </div>
            <p className="qr-instruction">Waiting for payment...</p>
          </div>

          <div className="powered-by">
            Powered by <strong>Crypto Cashier</strong>
          </div>
        </div>
      </div>
    );
  }

  // Cart view
  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <header className="checkout-header">
          <Bitcoin size={28} />
          <h1>Crypto Checkout</h1>
        </header>

        <div className="checkout-content">
          {/* Products */}
          <div className="products-section">
            <h2>Products</h2>
            <div className="products-grid">
              {products.map((product) => (
                <div key={product.id} className="product-card-mini">
                  {product.image_url && (
                    <div 
                      className="product-img" 
                      style={{backgroundImage: `url(${product.image_url})`}} 
                    />
                  )}
                  <div className="product-info-mini">
                    <h3>{product.name}</h3>
                    <p className="price">${product.price.toFixed(2)}</p>
                  </div>
                  <button 
                    className="add-btn-mini"
                    onClick={() => addToCart(product)}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Cart */}
          <div className="cart-section">
            <h2>
              <ShoppingCart size={20} />
              Your Cart ({cart.length})
            </h2>

            {cart.length === 0 ? (
              <div className="empty-cart-mini">
                <ShoppingCart size={48} color="rgba(228, 230, 235, 0.3)" />
                <p>Your cart is empty</p>
              </div>
            ) : (
              <>
                <div className="cart-items-list">
                  {cart.map((item) => (
                    <div key={item.product_id} className="cart-item-mini">
                      <div className="item-info">
                        <h4>{item.product_name}</h4>
                        <p>${item.price.toFixed(2)} each</p>
                      </div>
                      <div className="item-controls">
                        <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                          <Minus size={14} />
                        </button>
                        <span>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="item-total">${item.total.toFixed(2)}</div>
                      <button 
                        className="remove-btn"
                        onClick={() => updateQuantity(item.product_id, 0)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="cart-total-section">
                  <div className="total-row">
                    <span>Total:</span>
                    <span className="total-amount">${calculateTotal().toFixed(2)}</span>
                  </div>
                </div>

                <div className="email-input-section">
                  <label>Email (optional for receipt):</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                  />
                </div>

                <button 
                  className="checkout-btn"
                  onClick={handleCheckout}
                >
                  <CreditCard size={20} />
                  Pay with Crypto
                </button>
              </>
            )}

            <div className="powered-by-footer">
              Powered by <strong>Crypto Cashier</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
